import { AdaptationCycleStageConfig } from '../types'
import { Cog, Handshake, Landmark } from 'lucide-react'
import adaptationCycleStageFourImage from '@app/assets/illustrations/adaptation-cycle-overview-4.png'

export const adaptationCycleStageFour: AdaptationCycleStageConfig = {
  id: 'stage-four',
  title: 'Stage 4: Implementation and Monitoring',
  description:
    'Stage 4 ensures that adaptive strategies are put into practice. It embeds adaptation pathways within statutory plans, budgets, and asset programmes; coordinates delivery across partners; and operates the monitoring systems that connect real-world observations and changes to signals and triggers. It also carries the responsibility for actively re-evaluating whether the original tolerance thresholds still hold. As hazard profiles shift, exposure changes, and community resilience capacities change, the thresholds set in Stage 3 may need revision before a formal trigger is crossed.',
  tagline: '',
  heroImage: adaptationCycleStageFourImage,

  outcome: {
    title: 'Outcome of Stage 4',
    description:
      'An operational and adaptive implementation system that maintains accountability, enables course correction, and keeps adaptation aligned with evolving risks, revised thresholds, and community priorities. The three activities are sequential but cyclical. Implementation and integration (4.1) activates the adopted strategy. Monitoring and reporting (4.2) tracks both delivery progress and environmental change. Review and adjustment (4.3) closes the cycle, re-evaluates whether thresholds and objectives remain valid, and formally initiates the next iteration.',
  },
  topParagraphs: [
    'The primary objective of this stage is to ensure implementation through funded programmes, confirmed delivery responsibilities, and monitoring systems that flag when assumptions no longer hold. Whether Stage 4 can do that work depends on the groundwork laid earlier: the delivery handover in Activity 3.6, the statutory integration confirmed in Stage 3, and the partnerships built in Stage 1.',
  ],
  activities: [
    {
      activity: 'Activity 4.1',
      forumSlug: 'implementation-and-integration',
      title: 'Implement and Integrate',
      mfeAlignment: [{ step: 8, label: 'Implement the adaptive planning strategy' }],
      description:
        'This activity translates adopted focus area strategies into funded, deliverable programmes and integrates them across the organisation. It ensures that actions are embedded in statutory and financial instruments, delivery responsibilities are confirmed, and sequencing across focus areas is coordinated. The work here depends directly on the delivery handover completed in Activity 3.6: where that handover was clear and well-resourced, implementation can begin with momentum; where it was incomplete, the gaps will surface here.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Approve the consolidated implementation programme and confirm sequencing across focus areas.',
            'Endorse integration of actions into Long-Term Plans, infrastructure and financial strategies, Asset Management Plans, regional spatial and land-use plans, CDEM group plans, water-services strategies, stormwater risk-management plans, and relevant iwi and hapū planning documents.',
            'Maintain oversight of delivery progress and ensure consistency with the objectives established in Stage 1.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Confirm partner roles in delivery and resourcing, including iwi, infrastructure providers, and community organisations.',
            'Finalise agreements that operationalise shared responsibilities and funding arrangements.',
            'Communicate the integrated implementation programme and delivery schedule to partners and communities.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Embed near-term actions and budgets within existing work programmes and financial systems.',
            'Develop delivery plans specifying timing, responsible units, procurement approach, and performance measures.',
            'Coordinate dependencies between focus areas and assets (e.g. shared infrastructure or co-funded works).',
            'Establish internal reporting processes to track implementation and feed into monitoring (4.2).',
          ],
        },
      ],
      outputs: [
        {
          text: 'Integrated implementation programme covering all focus areas.',
        },
        {
          text: 'Confirmed delivery responsibilities, budgets, and timeframes.',
        },
        {
          text: 'Actions embedded within statutory and strategic instruments (LTPs, AMPs, iwi and hapu plans, district and regional plans).',
        },
        {
          text: 'Internal reporting framework for progress tracking.',
        },
      ],
      connections: [
        'Implements strategies adopted in Activity 3.6 and ensures they are embedded in statutory, financial, and organisational processes.',
        'Provides the baseline for monitoring (4.2) and the framework for review (4.3).',
        'Aligns focus area-level action with the wider adaptation objectives established in Activity 1.1.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-three',
          activity: 'Activity 3.6',
          label: '3.6',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.2',
          label: '4.2',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.3',
          label: '4.3',
        },
        { stageId: 'stage-one', activity: 'Activity 1.1', label: '1.1' },
      ],
      moveOnWhen: [
        'Actions are embedded in organisational plans and budgets.',
        'Delivery responsibilities are confirmed and sequencing is agreed.',
        'Statutory embedding is complete.',
      ],
      resources: [
        {
          title: 'Planning for Implementation',
          articles: [
            {
              title: 'Risk Treatment',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/risk-treatment',
            },
            {
              title: 'Adaptive Capacity',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
            },
            {
              title: 'Dynamic Adaptive Policy Pathways (DAPP)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
            },
          ],
        },
        {
          title: 'Statutory and Governance Context',
          articles: [
            {
              title: 'Local vs Regional Roles Under the RMA',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/local-vs-regional-roles-under-the-rma',
            },
            {
              title: 'Central Government Roles in Resource Management',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/central-government-roles-in-resource-management',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 4.2',
      forumSlug: 'monitoring-and-reporting',
      title: 'Monitor and Report',
      mfeAlignment: [{ step: 9, label: 'Monitor the adaptive planning strategy' }],
      description:
        'This activity operates the monitoring and reporting arrangements established in Activity 3.5. It tracks implementation progress and the indicators associated with signals, triggers, and review points, and reports when conditions warrant escalation or reassessment. Monitoring should also capture significant events and emerging evidence that may affect assumptions, risk tolerance, or the continued suitability of the adopted strategy.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Receive regular progress and performance reports from delivery teams.',
            'Confirm that monitoring frameworks remain aligned with adopted triggers and thresholds.',
            'Support transparent reporting to elected members and the public.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Collaborate with iwi, mana whenua, and partner organisations on co-monitoring programmes.',
            'Support community participation in monitoring through citizen science programmes (e.g. regular coastline photography from fixed locations) and community-led tools for recording event-based impacts and tracking local conditions over time.',
            'Share monitoring results and learning across partners and communities.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Develop and maintain monitoring systems covering delivery progress, environmental indicators, and social and economic measures.',
            'Track signals against established triggers and thresholds, and flag emerging evidence or conditions that warrant review.',
            'Record and analyse significant events to identify early activation or acceleration of triggers.',
            'Maintain a consolidated monitoring database and prepare regular reports for governance and partners.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Operational monitoring system covering delivery progress (milestones, budgets, completion rates), environmental signals and triggers, and social, economic, and cultural indicators co-designed with iwi and mana whenua.',
        },
        {
          text: 'Central monitoring database linking environmental, social, and implementation data.',
        },
        {
          text: 'Regular progress and state-of-environment reports summarising trends, signals, and triggers.',
        },
        {
          text: 'Documented learning from significant events, including trigger activations or community responses.',
        },
      ],
      connections: [
        'Draws on signals, triggers, and indicators defined in Activity 3.5.',
        'Feeds monitoring data into review and adjustment (4.3).',
        'Provides a feedback mechanism for reassessing risk and updating hazard, exposure, or tolerance data in future iterations.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-three',
          activity: 'Activity 3.5',
          label: '3.5',
        },
        {
          stageId: 'stage-four',
          activity: 'Activity 4.3',
          label: '4.3',
        },
      ],
      moveOnWhen: [
        'Monitoring systems are operational and baseline data is established.',
        'Reporting cycles are underway, covering both delivery and environmental change.',
        'Results are being reported transparently to governance and partners.',
      ],
      resources: [
        {
          title: 'Evidence and Confidence',
          articles: [
            {
              title: 'Strength of Knowledge',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
            },
            {
              title: 'Sources of Uncertainty in Modelling',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/uncertainty-in-modelling',
            },
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
          ],
        },
        {
          title: 'Signals, Triggers, and Pathways',
          articles: [
            {
              title: 'Dynamic Adaptive Policy Pathways (DAPP)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
            },
            {
              title: 'Risk & Surprises',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/risk-and-surprises-the-black-swan',
            },
          ],
        },
        {
          title: 'Communicating Change',
          articles: [
            {
              title: 'Foundations of Risk Communication',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-communication',
            },
            {
              title: 'Framing Effects in Risk Communication',
              description: '',
              href: '/app/guide/fundamentals/decisions-and-behaviour/framing-effects-in-risk-communication',
            },
            {
              title: 'Risk Stories',
              description: '',
              href: '/app/guide/methods/overview/risk-stories',
            },
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
          ],
        },
      ],
    },
    {
      activity: 'Activity 4.3',
      forumSlug: 'review-and-adjustment',
      title: 'Review and Adjust',
      mfeAlignment: [{ step: 10, label: 'Review and adjust the adaptive planning strategy' }],
      description:
        'This activity evaluates whether the adopted strategy remains fit for purpose as conditions, evidence, and implementation experience change. It reviews objectives, actions, triggers, thresholds, and assumptions, and determines whether they should be retained, adjusted, or reconsidered. Review may be prompted by monitoring results, significant events, new evidence, changing community priorities, or implementation performance. It closes one adaptation cycle and sets the direction for the next, so future updates build on accumulated evidence and learning rather than starting again. For statutory adaptation plans, ongoing adaptive review should operate alongside any formal legislative review requirements.',
      streams: [
        // Keep to 3 streams
        {
          icon: Landmark,
          title: 'GOVERNANCE',
          points: [
            'Review whether objectives and priorities from Stage 1 remain relevant given new evidence and community expectations.',
            'Confirm when and how threshold reviews and programme updates will be initiated.',
            'Maintain a statutory review schedule recording the adoption date, the deadline for commencing the next formal review, and the deadline for completing it.',
            'Where a statutory review results in amendment, replacement, or renewed adoption of the adaptation plan, complete the applicable special consultative procedure.',
            'Endorse and document any refinements to governance or decision-making processes.',
          ],
        },
        {
          icon: Handshake,
          title: 'PARTNERSHIPS',
          points: [
            'Create structured space with iwi, mana whenua, and partners to reflect on outcomes and identify emerging priorities or knowledge needs.',
            'Share findings across focus areas and organisations to coordinate future updates.',
            'Maintain transparency with communities about what has been learned and what will change in the next cycle.',
          ],
        },
        {
          icon: Cog,
          title: 'TECHNICAL',
          points: [
            'Analyse monitoring results to determine where updates to data, risk assessments, or thresholds are required.',
            'Evaluate whether original tolerance thresholds remain valid, or whether changes in hazard profiles, exposure, or community resilience require revision before a formal trigger is crossed.',
            'Define the scope, timing, and responsibilities for initiating updates in the next cycle.',
            'Document review findings, rationale, and improvement opportunities.',
          ],
        },
      ],
      outputs: [
        {
          text: 'Review summary and learning report, identifying areas requiring update or refinement.',
        },
        {
          text: 'Decision log recording when and why specific elements (data, pathways, thresholds, triggers) will be reviewed in the next cycle.',
        },
        {
          text: 'Schedule for initiating updates, including timing (signal-based or time-based) and responsible teams.',
        },
        {
          text: 'Statutory review schedule and compliance record, where applicable.',
        },
        {
          text: 'Review report documenting whether the plan remains fit for purpose and whether it should be retained, amended, replaced, or withdrawn.',
        },
        {
          text: 'Improvement plan for process, governance, or engagement methods.',
        },
        {
          text: 'Public summary communicating key findings, lessons, and planned next steps.',
        },
      ],
      connections: [
        'Reviews may be activated by observed signals or at scheduled intervals, consistent with national guidance.',
        'Draws on monitoring results from Activity 4.2.',
        'Provides direction and scope for updates across all stages: Stage 1 (context and objectives), Stage 2 (hazard and risk assessments), Stage 3 (options and strategies), and Stage 4 (implementation and monitoring systems).',
        'Completes the adaptive cycle and formally initiates the next iteration.',
      ],
      relatedActivities: [
        {
          stageId: 'stage-four',
          activity: 'Activity 4.2',
          label: '4.2',
        },
        { stageId: 'stage-one', label: 'Stage 1' },
        { stageId: 'stage-two', label: 'Stage 2' },
        { stageId: 'stage-three', label: 'Stage 3' },
      ],
      moveOnWhen: [
        'Any statutory review and consultation requirements have been completed, or their future dates and responsibilities are recorded.',
        'Review findings are endorsed and documented.',
        "Next cycle's update schedule is confirmed and responsibilities are assigned.",
      ],
      resources: [
        {
          title: 'Adaptive Planning and Review',
          articles: [
            {
              title: 'Dynamic Adaptive Policy Pathways (DAPP)',
              description: '',
              href: '/app/guide/fundamentals/decision-making-and-evaluation/dynamic-adaptive-pathways-planning',
            },
            {
              title: 'Adaptive Capacity',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
            },
            {
              title: 'Risk Treatment',
              description: '',
              href: '/app/guide/fundamentals/adaptation-and-system-change/risk-treatment',
            },
          ],
        },
        {
          title: 'Uncertainty and Changing Conditions',
          articles: [
            {
              title: 'Types of Uncertainty',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
            },
            {
              title: 'Risk & Surprises',
              description: '',
              href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/risk-and-surprises-the-black-swan',
            },
            {
              title: 'Strength of Knowledge',
              description: '',
              href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
            },
            {
              title: 'Using Scenarios to Explore Uncertain Futures in Planning',
              description: '',
              href: '/app/guide/fundamentals/climate-futures-and-scenarios/using-scenarios-to-explore-uncertain-futures-in-planning',
            },
          ],
        },
        {
          title: 'Climate and Hazard Context',
          articles: [
            {
              title: 'Climate Change: What You Might Need to Think About',
              description: '',
              href: '/app/guide/insights/commentary-and-opinion/climate-change-what-you-might-need-to-think-about',
            },
            {
              title: 'Hazards and Environmental Change',
              description: '',
              href: '/app/guide/fundamentals/hazards-and-stressors/hazards-and-environmental-change',
            },
          ],
        },
        {
          title: 'Community Values and Equity',
          articles: [
            {
              title: 'Community Values in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/community-values-in-adaptation',
            },
            {
              title: 'Climate Justice in Adaptation',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/climate-justice-in-adaptation',
            },
            {
              title: 'Relational Risk, Values, and Worldviews',
              description: '',
              href: '/app/guide/fundamentals/values-participation-and-governance/relational-risk-values-and-worldviews-how-values-shape-what-we-see-as-at-risk',
            },
          ],
        },
      ],
    },
  ],
  previousStage: 'stage-three',
  nextStage: 'stage-one',
}
